import {
  getTenantIdByHost,
  ZOOCASA_TENANT_ID,
} from 'tenants';

declare global {
  interface Window {
    mapkit: typeof mapkit & {
      load(libraries: string | string[]): void;
      loadedLibraries: string[];
      __initialized?: boolean;
      __currentLanguage?: string;
    };
  }
}

// Callback to refresh MapKit with new locale
let mapKitRefreshCallback: ((locale: string) => void) | null = null;

let mapKitCorePromise: Promise<void> | null = null;
let currentLanguage: string = 'en';

export async function loadMapKitCore(language?: string) {
  if (typeof window === 'undefined') return;

  // If language is provided and different from current, we need to reinitialize
  if (language && language !== currentLanguage) {
    currentLanguage = language;
    // Reset the promise to force reinitialization
    mapKitCorePromise = null;
    // Clear the initialized flag
    if (window.mapkit) {
      window.mapkit.__initialized = false;
      window.mapkit.__currentLanguage = language;
    }
  }

  if (mapKitCorePromise) {
    return mapKitCorePromise;
  }

  mapKitCorePromise = (async () => {
    if (!window.mapkit) {
      // This promise resolves when the browser finishes downloading and evaluating MapKit JS.
      await new Promise(resolve => {
        const element = document.createElement('script');
        element.addEventListener('load', resolve, { once : true });
        element.src = 'https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.core.js';
        element.crossOrigin = 'async';
        document.head.appendChild(element);
      });

      await new Promise<void>(resolve => {
        const checkCondition = () => {
          if (window?.mapkit) {
            resolve();
          } else {
            setTimeout(checkCondition, 100);
          }
        };

        checkCondition();
      });
    }

    if (!window.mapkit.__initialized) {
      await new Promise<void>((resolve, reject) => {
        const initOptions: any = {
          authorizationCallback: function (done) {
            if (process.env.NEXT_PUBLIC_APPLE_MAPKIT_JWT_VERCEL) {
              done((process.env.NEXT_PUBLIC_APPLE_MAPKIT_JWT_VERCEL as string));
              return;
            }
            const tenant = getTenantIdByHost(location.hostname) ?? ZOOCASA_TENANT_ID;
            fetch(`/api/gettoken?tenant=${tenant}`)
              .then(r => r.text())
              .then(done);
          },
        };

        // Add language option if provided
        if (currentLanguage) {
          initOptions.language = currentLanguage;
        }

        mapkit.init(initOptions);
        mapkit.addEventListener('configuration-change', function(event) {
          switch (event.status) {
            case 'Initialized':
              console.debug('MapKit JS initialized and configured.');
              resolve();
              break;
            case 'Refreshed':
              console.debug('The MapKit JS configuration was updated.');
              resolve();
              break;
          }
        });

        mapkit.addEventListener('error', function(event) {
          switch (event.status) {
            case 'Unauthorized':
              console.error('The MapKit JS Initialized Was Unauthorized.');
              reject(event.status);
              break;
            case 'Too Many Requests':
              console.error('Exceeded number of requests allowed.');
              reject(event.status);
              break;
          }
        });
      });

      await new Promise<void>(resolve => {
        window?.mapkit.load('full-map');
        window?.mapkit.addEventListener('load' as any, function() {
          resolve();
        });
      });
      window.mapkit.__initialized = true;
      window.mapkit.__currentLanguage = currentLanguage;
    }
  })();

  return mapKitCorePromise;
}

// Function to set up the callback for refreshing MapKit locale
export function setMapKitRefreshCallback(callback: (locale: string) => void) {
  mapKitRefreshCallback = callback;
}

// Set up the default callback
setMapKitRefreshCallback(reinitializeMapKitWithLocale);

// Function to refresh MapKit with new locale
export function refreshMapKitLocale(locale: string) {
  if (mapKitRefreshCallback) {
    mapKitRefreshCallback(locale);
  }
}

// Function to reinitialize MapKit with new locale
function reinitializeMapKitWithLocale(locale: string) {
  // Force reinitialization with the new language
  loadMapKitCore(locale);
}